import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2, Baby, Save, Sparkles, FileDown, FileText, Calendar, Heart, User, Edit3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { PatientChild, VitalityAssessment } from "@/types/vitality";
import type { SessionReportItem } from "@/types/sessionReport";

interface ChildrenSectionProps {
  patientId: string;
  children: PatientChild[];
  onChildrenChange: (children: PatientChild[]) => void;
}

interface ChildAssessment {
  childId: string;
  assessments: VitalityAssessment[];
}

interface ChildSessionReport {
  id: string;
  accompaniment_title: string;
  report_date: string;
  child_id: string;
  bach_flowers_data?: any;
}

export function ChildrenSection({ patientId, children, onChildrenChange }: ChildrenSectionProps) {
  const [saving, setSaving] = useState(false);
  const [childAssessments, setChildAssessments] = useState<ChildAssessment[]>([]);
  const [childSessionReports, setChildSessionReports] = useState<ChildSessionReport[]>([]);
  const [expandedChild, setExpandedChild] = useState<string | null>(null);

  useEffect(() => {
    const loadChildData = async () => {
      const childIds = children.filter(c => c.id).map(c => c.id!);
      if (childIds.length === 0) {
        setChildAssessments([]);
        setChildSessionReports([]);
        return;
      }

      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from("vitality_assessments")
        .select("*")
        .in("child_id", childIds)
        .order("created_at", { ascending: false });

      if (!assessmentsError && assessmentsData) {
        const grouped = childIds.map(childId => ({
          childId,
          assessments: assessmentsData.filter(a => a.child_id === childId) as VitalityAssessment[]
        }));
        setChildAssessments(grouped);
      }

      const { data: reportsData, error: reportsError } = await supabase
        .from("session_reports")
        .select("id, accompaniment_title, report_date, child_id, bach_flowers_data")
        .in("child_id", childIds)
        .order("created_at", { ascending: false });

      if (!reportsError && reportsData) {
        setChildSessionReports(reportsData as ChildSessionReport[]);
      }
    };

    loadChildData();
  }, [children]);

  const getChildAssessments = (childId: string): VitalityAssessment[] => {
    return childAssessments.find(ca => ca.childId === childId)?.assessments || [];
  };

  const getChildSessionReports = (childId: string): ChildSessionReport[] => {
    return childSessionReports.filter(report => report.child_id === childId);
  };

  const deleteChildSessionReport = async (reportId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce compte rendu de séance ?")) return;
    
    const { error } = await supabase
      .from("session_reports")
      .delete()
      .eq("id", reportId);

    if (error) {
      toast.error("Erreur lors de la suppression");
      return;
    }

    setChildSessionReports(prev => prev.filter(r => r.id !== reportId));
    toast.success("Compte rendu supprimé");
  };

  const calculateAge = (dateOfBirth: string): string => {
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  const getAgeLabel = (dateOfBirth: string): string => {
    const age = parseInt(calculateAge(dateOfBirth));
    if (age < 1) return "Nourrisson";
    if (age === 1) return "1 an";
    return `${age} ans`;
  };

  const deleteChildAssessment = async (assessmentId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce bilan de vitalité ?")) return;
    
    const { error } = await supabase
      .from("vitality_assessments")
      .delete()
      .eq("id", assessmentId);

    if (error) {
      toast.error("Erreur lors de la suppression");
      return;
    }

    setChildAssessments(prev => prev.map(ca => ({
      ...ca,
      assessments: ca.assessments.filter(a => a.id !== assessmentId)
    })));
    toast.success("Bilan supprimé");
  };

  const addChild = () => {
    onChildrenChange([
      ...children,
      {
        parent_id: patientId,
        name: "",
        date_of_birth: "",
        gender: "",
        phone: "",
        email: "",
        address: "",
        medical_history: "",
      },
    ]);
  };

  const removeChild = async (index: number) => {
    const child = children[index];
    if (child.id) {
      if (!confirm("Êtes-vous sûr de vouloir supprimer cet enfant ?")) return;
      
      const { error } = await supabase.from("patient_children").delete().eq("id", child.id);
      if (error) {
        toast.error("Erreur lors de la suppression");
        return;
      }
      toast.success("Enfant supprimé");
    }
    onChildrenChange(children.filter((_, i) => i !== index));
  };

  const updateChild = (index: number, field: keyof PatientChild, value: string) => {
    const newChildren = [...children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    onChildrenChange(newChildren);
  };

  const saveChild = async (index: number) => {
    const child = children[index];
    
    if (!child.name.trim()) {
      toast.error("Le nom de l'enfant est requis");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      const childData = {
        ...child,
        parent_id: patientId,
        user_id: user.id,
      };

      if (child.id) {
        const { error } = await supabase
          .from("patient_children")
          .update(childData)
          .eq("id", child.id);

        if (error) throw error;
        toast.success("Enfant mis à jour");
      } else {
        const { data, error } = await supabase
          .from("patient_children")
          .insert([childData])
          .select()
          .single();

        if (error) throw error;
        
        const newChildren = [...children];
        newChildren[index] = data;
        onChildrenChange(newChildren);
        toast.success("Enfant enregistré");
      }
    } catch (error: any) {
      toast.error("Erreur lors de l'enregistrement", { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  const getGenderIcon = (gender: string | undefined) => {
    switch (gender) {
      case "femme": return "♀";
      case "homme": return "♂";
      default: return null;
    }
  };

  const getGenderColor = (gender: string | undefined) => {
    switch (gender) {
      case "femme": return "text-pink-500";
      case "homme": return "text-blue-500";
      default: return "text-muted-foreground";
    }
  };

  return (
    <Card className="shadow-card overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Baby className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-base sm:text-lg">Enfants du consultant</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {children.length === 0 
                  ? "Aucun enfant enregistré" 
                  : `${children.length} enfant${children.length > 1 ? "s" : ""} associé${children.length > 1 ? "s" : ""}`
                }
              </CardDescription>
            </div>
          </div>
          <Button onClick={addChild} size="sm" className="bg-violet-600 hover:bg-violet-700 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-6">
        {children.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Baby className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">Aucun enfant enregistré pour ce consultant</p>
            <p className="text-xs text-muted-foreground mt-1">Cliquez sur "Ajouter" pour créer une fiche enfant</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {children.map((child, index) => {
              const assessmentCount = child.id ? getChildAssessments(child.id).length : 0;
              const reportCount = child.id ? getChildSessionReports(child.id).length : 0;
              const isExpanded = expandedChild === (child.id || `new-${index}`);
              
              return (
                <Card 
                  key={child.id || `new-${index}`} 
                  className={`transition-all duration-200 ${isExpanded ? 'ring-2 ring-violet-300 dark:ring-violet-700' : 'hover:shadow-md'}`}
                >
                  {/* Child Header - Always visible */}
                  <div 
                    className="p-3 sm:p-4 cursor-pointer"
                    onClick={() => setExpandedChild(isExpanded ? null : (child.id || `new-${index}`))}
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                        child.gender === "femme" 
                          ? "bg-gradient-to-br from-pink-100 to-pink-200 dark:from-pink-900/30 dark:to-pink-800/30" 
                          : child.gender === "homme"
                            ? "bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30"
                            : "bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700"
                      }`}>
                        {child.gender ? (
                          <span className={`text-xl sm:text-2xl font-bold ${getGenderColor(child.gender)}`}>
                            {getGenderIcon(child.gender)}
                          </span>
                        ) : (
                          <User className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-sm sm:text-base truncate">
                            {child.name || "Nouvel enfant"}
                          </h4>
                          {!child.id && (
                            <Badge variant="secondary" className="text-[10px] sm:text-xs">Non enregistré</Badge>
                          )}
                        </div>
                        
                        {child.date_of_birth && (
                          <div className="flex items-center gap-3 mt-1 text-xs sm:text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {getAgeLabel(child.date_of_birth)}
                            </span>
                            <span className="hidden sm:inline">
                              (né le {new Date(child.date_of_birth).toLocaleDateString("fr-FR")})
                            </span>
                          </div>
                        )}
                        
                        {/* Stats badges */}
                        {child.id && (assessmentCount > 0 || reportCount > 0) && (
                          <div className="flex gap-2 mt-2">
                            {assessmentCount > 0 && (
                              <Badge variant="outline" className="text-[10px] sm:text-xs bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400 border-emerald-200">
                                <Sparkles className="w-3 h-3 mr-1" />
                                {assessmentCount} bilan{assessmentCount > 1 ? "s" : ""}
                              </Badge>
                            )}
                            {reportCount > 0 && (
                              <Badge variant="outline" className="text-[10px] sm:text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border-blue-200">
                                <FileText className="w-3 h-3 mr-1" />
                                {reportCount} séance{reportCount > 1 ? "s" : ""}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Expand indicator */}
                      <div className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t">
                      <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
                        {/* Form Fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs sm:text-sm">Nom complet *</Label>
                            <Input
                              value={child.name}
                              onChange={(e) => updateChild(index, "name", e.target.value)}
                              placeholder="Prénom Nom"
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs sm:text-sm">Date de naissance</Label>
                            <Input
                              type="date"
                              value={child.date_of_birth || ""}
                              onChange={(e) => updateChild(index, "date_of_birth", e.target.value)}
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs sm:text-sm">Genre biologique</Label>
                            <Select 
                              value={child.gender || "none"} 
                              onValueChange={(value) => updateChild(index, "gender", value === "none" ? "" : value)}
                            >
                              <SelectTrigger className="h-9 sm:h-10 text-sm">
                                <SelectValue placeholder="Sélectionner..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Non renseigné</SelectItem>
                                <SelectItem value="femme">Femme</SelectItem>
                                <SelectItem value="homme">Homme</SelectItem>
                                <SelectItem value="intersexe">Intersexe</SelectItem>
                                <SelectItem value="prefere_ne_pas_repondre">Préfère ne pas répondre</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs sm:text-sm">Téléphone</Label>
                            <Input
                              type="tel"
                              value={child.phone || ""}
                              onChange={(e) => updateChild(index, "phone", e.target.value)}
                              placeholder="06 12 34 56 78"
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs sm:text-sm">Email</Label>
                            <Input
                              type="email"
                              value={child.email || ""}
                              onChange={(e) => updateChild(index, "email", e.target.value)}
                              placeholder="email@exemple.com"
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-xs sm:text-sm">Adresse</Label>
                            <Input
                              value={child.address || ""}
                              onChange={(e) => updateChild(index, "address", e.target.value)}
                              placeholder="Adresse"
                              className="h-9 sm:h-10 text-sm"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs sm:text-sm flex items-center gap-2">
                            <Heart className="w-3 h-3 text-red-500" />
                            Antécédents médicaux
                          </Label>
                          <Textarea
                            value={child.medical_history || ""}
                            onChange={(e) => updateChild(index, "medical_history", e.target.value)}
                            placeholder="Historique médical de l'enfant..."
                            rows={2}
                            className="text-sm"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs sm:text-sm">Informations complémentaires</Label>
                          <Textarea
                            value={child.additional_info || ""}
                            onChange={(e) => updateChild(index, "additional_info", e.target.value)}
                            placeholder="Toute information complémentaire utile..."
                            rows={2}
                            className="text-sm"
                          />
                        </div>

                        {/* Bilans de vitalité */}
                        {child.id && (
                          <div className="pt-4 border-t space-y-3">
                            <h4 className="font-medium text-sm flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <Sparkles className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                              </div>
                              Bilans de vitalité
                            </h4>
                            {getChildAssessments(child.id).length === 0 ? (
                              <p className="text-muted-foreground text-xs sm:text-sm pl-8">Aucun bilan enregistré</p>
                            ) : (
                              <div className="space-y-2 pl-0 sm:pl-8">
                                {getChildAssessments(child.id).map((assessment) => (
                                  <Card key={assessment.id} className="bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-200/50">
                                    <CardContent className="p-3">
                                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <div>
                                          <div className="font-medium text-xs sm:text-sm">
                                            {assessment.created_at
                                              ? new Date(assessment.created_at).toLocaleDateString("fr-FR", {
                                                  day: "numeric",
                                                  month: "long",
                                                  year: "numeric",
                                                })
                                              : "Date inconnue"}
                                          </div>
                                          {assessment.consultation_reason && (
                                            <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                                              {assessment.consultation_reason.substring(0, 50)}...
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex gap-2 self-end sm:self-center">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                              try {
                                                const { generateVitalityAssessmentPDF } = await import("@/utils/pdfExport");
                                                const childAge = child.date_of_birth ? calculateAge(child.date_of_birth) : undefined;
                                                generateVitalityAssessmentPDF(
                                                  assessment,
                                                  "",
                                                  { name: child.name, age: childAge, gender: child.gender }
                                                );
                                                toast.success("PDF généré");
                                              } catch (error) {
                                                toast.error("Erreur lors de la génération du PDF");
                                              }
                                            }}
                                            className="h-7 text-xs gap-1"
                                          >
                                            <FileDown className="w-3 h-3" />
                                            PDF
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => deleteChildAssessment(assessment.id!)}
                                            className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Comptes rendus de séance */}
                        {child.id && (
                          <div className="pt-4 border-t space-y-3">
                            <h4 className="font-medium text-sm flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <FileText className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                              </div>
                              Comptes rendus de séance
                            </h4>
                            {getChildSessionReports(child.id).length === 0 ? (
                              <p className="text-muted-foreground text-xs sm:text-sm pl-8">Aucun compte rendu enregistré</p>
                            ) : (
                              <div className="space-y-2 pl-0 sm:pl-8">
                                {getChildSessionReports(child.id).map((report) => (
                                  <Card key={report.id} className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-200/50">
                                    <CardContent className="p-3">
                                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <div>
                                          <div className="font-medium text-xs sm:text-sm flex items-center gap-2 flex-wrap">
                                            {report.accompaniment_title}
                                            {report.bach_flowers_data && (
                                              <Badge variant="outline" className="text-[10px]">Bach</Badge>
                                            )}
                                          </div>
                                          <div className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                                            {new Date(report.report_date).toLocaleDateString("fr-FR", {
                                              day: "numeric",
                                              month: "long",
                                              year: "numeric",
                                            })}
                                          </div>
                                        </div>
                                        <div className="flex gap-2 self-end sm:self-center">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                              try {
                                                const { downloadSessionReportPDF } = await import("@/utils/sessionReportPdfExport");
                                                const childAge = child.date_of_birth ? calculateAge(child.date_of_birth) : undefined;
                                                
                                                const { data: fullReport } = await supabase
                                                  .from("session_reports")
                                                  .select("*")
                                                  .eq("id", report.id)
                                                  .single();
                                                
                                                if (fullReport) {
                                                  const { data: parentData } = await supabase
                                                    .from("patients")
                                                    .select("name")
                                                    .eq("id", patientId)
                                                    .single();
                                                  
                                                  downloadSessionReportPDF(
                                                    {
                                                      ...fullReport,
                                                      hygiene_vie: Array.isArray(fullReport.hygiene_vie) ? fullReport.hygiene_vie as unknown as SessionReportItem[] : [],
                                                      conseils_alimentaires: Array.isArray(fullReport.conseils_alimentaires) ? fullReport.conseils_alimentaires as unknown as SessionReportItem[] : [],
                                                      soutiens_naturels: Array.isArray(fullReport.soutiens_naturels) ? fullReport.soutiens_naturels as unknown as SessionReportItem[] : [],
                                                      routine_bien_etre: Array.isArray(fullReport.routine_bien_etre) ? fullReport.routine_bien_etre as unknown as SessionReportItem[] : [],
                                                    },
                                                    parentData?.name || "",
                                                    undefined,
                                                    { name: child.name, age: childAge, gender: child.gender }
                                                  );
                                                  toast.success("PDF généré");
                                                }
                                              } catch (error) {
                                                console.error("Error generating PDF:", error);
                                                toast.error("Erreur lors de la génération du PDF");
                                              }
                                            }}
                                            className="h-7 text-xs gap-1"
                                          >
                                            <FileDown className="w-3 h-3" />
                                            PDF
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => deleteChildSessionReport(report.id)}
                                            className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeChild(index)}
                            className="text-destructive hover:text-destructive w-full sm:w-auto"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveChild(index)}
                            disabled={saving}
                            className="gap-2 bg-violet-600 hover:bg-violet-700 w-full sm:w-auto"
                          >
                            <Save className="w-4 h-4" />
                            {saving ? "Enregistrement..." : "Enregistrer"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
